import type { IntlShape } from "react-intl";
import type { I18nFunction } from "shared-utils";
import { camelToSpace } from "shared-utils";

import {
  formatPhoneNumberForDisplay,
  type CountryMap,
  type DisplayRole,
  type IdentityVerificationStatusSchema,
  type IdvSimpleStatus,
  type JurisdictionalLabelGetter,
  type PartialRecord,
  type SupportedCountry,
} from "..";
import type { DEAssociatedPersonJurisdictionalDataSchema } from "../api-types/jurisdictions/de_schemas";
import type {
  AnnualIncome,
  AnnualTurnoverCode,
  Occupation,
  SourceOfIncome,
  TotalEmployeesCode,
  USAssociatedPersonJurisdictionalDataSchema,
} from "../api-types/jurisdictions/us_schemas";
import type { Role } from "../api-types/staff_app_schemas";

export * from "./address-helpers";
export * from "./companyValidation";
export * from "./contractSignerValidation";
export * from "./countries";
export * from "./extractDocumentRequirements";
export * from "./format-helpers";
export * from "./getJurisdictionIDVService";
export * from "./idValidation";
export * from "./isInViewport";
export * from "./kyc-maps";
export * from "./luhnValidation";
export * from "./mapping-data-helpers";
type JurisdictionalLabelGetters = {
  getPostCodeLabel: JurisdictionalLabelGetter;
  getIdLabel: JurisdictionalLabelGetter;
};

export const NO_EXECUTIVE_ROLE = "No Executive Role";

export const KYC_HELP_CENTER_URL = "https://wayflyer.com/help-center/business-verification";

export function getJurisdictionalLabelGetters(t: I18nFunction = (str) => str): JurisdictionalLabelGetters {
  const postcodes: CountryMap = {
    US: t("ZIP code"),
    IE: t("Eircode"),
    GB: t("Postcode"),
    AU: t("Postcode"),
    CA: t("Postcode"),
    ES: t("Postcode"),
    NL: t("Postcode"),
    BE: t("Postcode"),
    DK: t("Postcode"),
    SE: t("Postcode"),
    NZ: t("Postcode"),
    DE: t("Postcode"),
    FR: t("Postcode"),
  };
  const idTypes: CountryMap = {
    US: t("US Social Security Number"),
    IE: t("Irish PPS Number"),
    GB: t("UK National Insurance Number"),
    AU: t("Australian Tax File Number"),
    CA: t("Canadian Social Insurance Number"),
    ES: t("Spanish Número de Identificación Fiscal"),
    NL: t("Dutch Burgerservicenummer"),
    BE: t("Belgian National Register number"),
    DK: t("Danish CPR number"),
    SE: t("Swedish Personnummer"),
    NZ: t("New Zealander NHI number"), // N.B. Not used atm
    DE: t("German Steuer-Identifikationsnummer"),
    FR: t("French Numéro Fiscal de Référence"),
  };
  return {
    getPostCodeLabel: (country: SupportedCountry) => postcodes[country] ?? t("Postal code"),
    getIdLabel: (country: SupportedCountry) => idTypes[country] ?? t("Local Tax ID Number"),
  };
}

export const getDisplayJurisdictionSpecificPersonFields = (country: SupportedCountry, isV2Flow: boolean) => {
  const lookup: PartialRecord<SupportedCountry, (value) => Array<PartialRecord<string, string | number>>> = {
    DE: (jurisdictionData: DEAssociatedPersonJurisdictionalDataSchema) => [
      { Gender: jurisdictionData.gender },
      { "Tax Residency": jurisdictionData.tax_residency },
      { "Power of attorney": jurisdictionData.power_of_attorney ?? undefined },
      {
        "UBO ownership percentage":
          jurisdictionData.ubo_ownership_percentage != null
            ? `${jurisdictionData.ubo_ownership_percentage}%`
            : undefined,
      },
      {
        "Proof of address provided?": jurisdictionData.proof_of_address_provided ?? undefined,
      },
    ],
    US: (jurisdictionData?: USAssociatedPersonJurisdictionalDataSchema) => [
      {
        "Phone number": formatPhoneNumberForDisplay(jurisdictionData?.phone_number ?? undefined) ?? undefined,
      },
      {
        "UBO ownership percentage":
          jurisdictionData?.ubo_ownership_percentage != null ? `${jurisdictionData.ubo_ownership_percentage}%` : "",
      },
      ...(isV2Flow
        ? [
            {
              "Passport number": jurisdictionData?.passport_number ?? "",
            },
            {
              Occupation: camelToSpace(jurisdictionData?.occupation as string) ?? "",
            },
            {
              "Annual income": camelToSpace(jurisdictionData?.annual_income as string) ?? "",
            },
            {
              "Source of income": camelToSpace(jurisdictionData?.source_of_income as string) ?? "",
            },
          ]
        : []),
    ],
  };
  return lookup[country];
};

export function getSimpleIdvStatus(idvStatus?: IdentityVerificationStatusSchema): IdvSimpleStatus {
  if (!idvStatus) {
    return "not_started";
  }
  const { status, last_error } = idvStatus;

  if (status === "requires_input" && last_error) {
    return "failed";
  }

  return status === "verified" || status === "fraud" ? status : "not_verified";
}

export function isExecutive(role: Role) {
  return role !== NO_EXECUTIVE_ROLE;
}

export function getDisplayRole(role: Role, isUbo: boolean): DisplayRole {
  if (role === "No Executive Role") {
    return isUbo ? "UBO" : "No significant role";
  }

  return isUbo ? `${role}, UBO` : role;
}

export function requiresIdv(role, isUbo, isSignee) {
  return isExecutive(role) || isUbo || isSignee;
}

export function concatTruthyFields(fields: (string | undefined | null)[], separator = " "): string {
  return fields.filter(Boolean).join(separator);
}

export const getRoleOptions = (intl: IntlShape): Array<{ id: Role; text: string }> => [
  {
    id: "Chief Executive Officer",
    text: intl.formatMessage({
      id: "Chief Executive Officer (CEO)",
      defaultMessage: "Chief Executive Officer (CEO)",
    }),
  },
  {
    id: "Chief Financial Officer",
    text: intl.formatMessage({
      id: "Chief Financial Officer (CFO)",
      defaultMessage: "Chief Financial Officer (CFO)",
    }),
  },
  {
    id: "Chief Operating Officer",
    text: intl.formatMessage({
      id: "Chief Operating Officer (COO)",
      defaultMessage: "Chief Operating Officer (COO)",
    }),
  },
  {
    id: "Managing Member",
    text: intl.formatMessage({ id: "Managing Member", defaultMessage: "Managing Member" }),
  },
  {
    id: "General Partner",
    text: intl.formatMessage({ id: "General Partner", defaultMessage: "General Partner" }),
  },
  { id: "President", text: intl.formatMessage({ id: "President", defaultMessage: "President" }) },
  { id: "Vice President", text: intl.formatMessage({ id: "Vice President", defaultMessage: "Vice President" }) },
  { id: "Treasurer", text: intl.formatMessage({ id: "Treasurer", defaultMessage: "Treasurer" }) },
  {
    id: "No Executive Role",
    text: intl.formatMessage({ id: "No significant role", defaultMessage: "No significant role" }),
  },
];

export const getOccupationOptions = (intl: IntlShape): Array<{ id: Occupation; text: string }> => [
  {
    id: "ArchitectOrEngineer",
    text: intl.formatMessage({ id: "Architect Or Engineer", defaultMessage: "Architect Or Engineer" }),
  },
  {
    id: "BusinessAnalystAccountantOrFinancialAdvisor",
    text: intl.formatMessage({
      id: "Business Analyst Accountant Or Financial Advisor",
      defaultMessage: "Business Analyst Accountant Or Financial Advisor",
    }),
  },
  {
    id: "CommunityAndSocialServicesWorker",
    text: intl.formatMessage({
      id: "Community And Social Services Worker",
      defaultMessage: "Community And Social Services Worker",
    }),
  },
  {
    id: "ConstructionMechanicOrMaintenanceWorker",
    text: intl.formatMessage({
      id: "Construction Mechanic Or Maintenance Worker",
      defaultMessage: "Construction Mechanic Or Maintenance Worker",
    }),
  },
  { id: "Doctor", text: intl.formatMessage({ id: "Doctor", defaultMessage: "Doctor" }) },
  { id: "Educator", text: intl.formatMessage({ id: "Educator", defaultMessage: "Educator" }) },
  {
    id: "EntertainmentSportsArtsOrMedia",
    text: intl.formatMessage({
      id: "Entertainment Sports Arts Or Media",
      defaultMessage: "Entertainment Sports Arts Or Media",
    }),
  },
  {
    id: "ExecutiveOrManager",
    text: intl.formatMessage({ id: "Executive Or Manager", defaultMessage: "Executive Or Manager" }),
  },
  {
    id: "FarmerFishermanForester",
    text: intl.formatMessage({ id: "Farmer Fisherman Forester", defaultMessage: "Farmer Fisherman Forester" }),
  },
  {
    id: "FoodServiceWorker",
    text: intl.formatMessage({ id: "Food Service Worker", defaultMessage: "Food Service Worker" }),
  },
  { id: "GigWorker", text: intl.formatMessage({ id: "Gig Worker", defaultMessage: "Gig Worker" }) },
  {
    id: "HospitalityOfficeOrAdministrativeSupportWorker",
    text: intl.formatMessage({
      id: "Hospitality Office Or Administrative Support Worker",
      defaultMessage: "Hospitality Office Or Administrative Support Worker",
    }),
  },
  {
    id: "HouseholdManager",
    text: intl.formatMessage({ id: "Household Manager", defaultMessage: "Household Manager" }),
  },
  {
    id: "JanitorHousekeeperLandscaper",
    text: intl.formatMessage({
      id: "Janitor Housekeeper Landscaper",
      defaultMessage: "Janitor Housekeeper Landscaper",
    }),
  },
  { id: "Lawyer", text: intl.formatMessage({ id: "Lawyer", defaultMessage: "Lawyer" }) },
  {
    id: "ManufacturingOrProductionWorker",
    text: intl.formatMessage({
      id: "Manufacturing Or Production Worker",
      defaultMessage: "Manufacturing Or Production Worker",
    }),
  },
  {
    id: "MilitaryOrPublicSafety",
    text: intl.formatMessage({ id: "Military Or Public Safety", defaultMessage: "Military Or Public Safety" }),
  },
  {
    id: "NurseHealthcareTechnicianOrHealthcareSupport",
    text: intl.formatMessage({
      id: "Nurse Healthcare Technician Or Healthcare Support",
      defaultMessage: "Nurse Healthcare Technician Or Healthcare Support",
    }),
  },
  {
    id: "PersonalCareOrServiceWorker",
    text: intl.formatMessage({
      id: "Personal Care Or Service Worker",
      defaultMessage: "Personal Care Or Service Worker",
    }),
  },
  {
    id: "PilotDriverOperator",
    text: intl.formatMessage({ id: "Pilot Driver Operator", defaultMessage: "Pilot Driver Operator" }),
  },
  {
    id: "SalesRepresentativeBrokerAgent",
    text: intl.formatMessage({
      id: "Sales Representative Broker Agent",
      defaultMessage: "Sales Representative Broker Agent",
    }),
  },
  {
    id: "ScientistOrTechnologist",
    text: intl.formatMessage({ id: "Scientist Or Technologist", defaultMessage: "Scientist Or Technologist" }),
  },
  { id: "Student", text: intl.formatMessage({ id: "Student", defaultMessage: "Student" }) },
];

export const getAnnualIncomeOptions = (intl: IntlShape): Array<{ id: AnnualIncome; text: string }> => [
  {
    id: "UpTo10k",
    text: intl.formatMessage({ id: "Less Than $10,000", defaultMessage: "Less Than $10,000" }),
  },
  {
    id: "Between10kAnd25k",
    text: intl.formatMessage({ id: "$10,000 - $25,000", defaultMessage: "$10,000 - $25,000" }),
  },
  {
    id: "Between25kAnd50k",
    text: intl.formatMessage({ id: "$25,000 - $50,000", defaultMessage: "$25,000 - $50,000" }),
  },
  {
    id: "Between50kAnd100k",
    text: intl.formatMessage({ id: "$50,000 - $100,000", defaultMessage: "$50,000 - $100,000" }),
  },
  {
    id: "Between100kAnd250k",
    text: intl.formatMessage({ id: "$100,000 - $250,000", defaultMessage: "$100,000 - $250,000" }),
  },
  {
    id: "Over250k",
    text: intl.formatMessage({ id: "Greater Than $250,000", defaultMessage: "Greater Than $250,000" }),
  },
];

export const getTotalEmployeesOptions = (intl: IntlShape): Array<{ id: TotalEmployeesCode; text: string }> => [
  {
    id: "EM006",
    text: intl.formatMessage({ id: "1 employee", defaultMessage: "1" }),
  },
  {
    id: "EM007",
    text: intl.formatMessage({ id: "2 to 5 employees", defaultMessage: "2 - 5" }),
  },
  {
    id: "EM008",
    text: intl.formatMessage({ id: "6 to 24 employees", defaultMessage: "6 - 24" }),
  },
  {
    id: "EM009",
    text: intl.formatMessage({ id: "Greater than 24 employees", defaultMessage: "Greater than 24" }),
  },
];

export const getAnnualTurnoverOptions = (intl: IntlShape): Array<{ id: AnnualTurnoverCode; text: string }> => [
  {
    id: "US008",
    text: intl.formatMessage({ id: "Annual turnover less than USD 100,000", defaultMessage: "Less than $100,000" }),
  },
  {
    id: "US009",
    text: intl.formatMessage({
      id: "Annual turnover USD 100,000 to USD 500,000",
      defaultMessage: "$100,000 - $500,000",
    }),
  },
  {
    id: "US010",
    text: intl.formatMessage({
      id: "Annual turnover USD 500,000 to USD 1,500,000",
      defaultMessage: "$500,000 - $1,500,000",
    }),
  },
  {
    id: "US011",
    text: intl.formatMessage({ id: "Annual turnover USD 1,500,000+", defaultMessage: "Greater than $1,500,000" }),
  },
];

export const getSourceOfIncomeOptions = (intl: IntlShape): Array<{ id: SourceOfIncome; text: string }> => [
  {
    id: "EmploymentOrPayrollIncome",
    text: intl.formatMessage({ id: "Employment Or Payroll Income", defaultMessage: "Employment Or Payroll Income" }),
  },
  {
    id: "PartTimeOrContractorIncome",
    text: intl.formatMessage({
      id: "Part Time Or Contractor Income",
      defaultMessage: "Part Time Or Contractor Income",
    }),
  },
  {
    id: "InheritancesAndGifts",
    text: intl.formatMessage({ id: "Inheritances And Gifts", defaultMessage: "Inheritances And Gifts" }),
  },
  {
    id: "PersonalInvestments",
    text: intl.formatMessage({ id: "Personal Investments", defaultMessage: "Personal Investments" }),
  },
  {
    id: "BusinessOwnershipInterests",
    text: intl.formatMessage({ id: "Business Ownership Interests", defaultMessage: "Business Ownership Interests" }),
  },
  {
    id: "GovernmentBenefits",
    text: intl.formatMessage({ id: "Government Benefits", defaultMessage: "Government Benefits" }),
  },
];
